import {
    Box,
    IconButton,
    ListItemIcon,
    ListItemText,
    Menu,
    MenuItem,
    Tooltip,
    Typography,
} from '@mui/material';
import {
    CopyPlus,
    EditIcon,
    Focus,
    Info,
    MoreVertical,
    Replace,
    Trash2,
} from 'lucide-react';
import React, { useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import CollapsibleBottomPanel from '~/components/CollapsibleBottomPanel';
import InlineEdit from '~/components/InlineEdit';
import NestedMenuItem from '~/components/Menu/NestedMenuItem';
import PopperPanel from '~/components/PopperPanel';
import StructureColorPicker from '../ColorPicker';
import {
    DICOMApprovalStatus,
    isApprovalStatusEditable,
} from '~/domain/dicom/dicomConstants';
import { RoiId } from '~/domain/changes/ids';
import { RoiReadModel } from '~/domain/readmodels/StructureSetReadModel';
import { useRoiDetailView } from '~/hooks/domain/useRoiDetailView';
import useDoseTargetLookup from '~/hooks/patient/dose/useDoseTargetLookup';
import useRoi from '~/hooks/structure-set/useRoi';
import useStructureNameValidation from '~/hooks/structure-set/useStructureNameValidation';
import useViewportFocus from '~/hooks/useViewportFocus';
import { openedStructureDeletionConfirmationDialogState } from '~/state/atoms/structureSet/roiAtoms';
import { currentStructureSetState } from '~/state/atoms/structureSet/structureSetAtoms';
import ContourStatistics from './ContourStatistics';
import StructureDetailsInformation from './StructureDetailsInformation';
import StructureInformation from './StructureInformation';

interface StructureDetailsProps {
    roi: RoiReadModel;
    /** Branded RoiId from the projection layer. When present, volumeCm3 and
     * isEditable are read from useRoiDetailView (new path). Falls back to
     * the legacy useRoi path when absent. */
    roiId?: RoiId;
}

export default function StructureDetails({
    roi,
    roiId,
}: StructureDetailsProps) {
    const intl = useIntl();
    const {
        setInterpretedType,
        setPointCoordinates,
        getRoiVolumeCm3: legacyGetRoiVolumeCm3,
        getRoiEquivalentSphereDiameterCm: legacyGetEquivalentSphereDiameterCm,
        isRoiEmpty,
        copyRoi,
        copySdfOver,
        setName,
        setColor,
        setVisible,
    } = useRoi(roi);

    // Projection hook — provides volumeCm3 (already cm³) and isEditable
    // derived from approval status. Falls back to legacy path when roiId is
    // not yet resolved (e.g. during store initialisation).
    const detailView = useRoiDetailView(roiId);
    const { focusOnStructure, focusOnPoint } = useViewportFocus();
    const setOpenedStructureDeletionConfirmationDialog = useSetRecoilState(
        openedStructureDeletionConfirmationDialogState
    );
    const selectedStructureSet = useRecoilValue(currentStructureSetState);
    const [isRenaming, setIsRenaming] = useState(false);
    const colorPickerAnchorRef = useRef<HTMLDivElement>(null);
    const validateName = useStructureNameValidation(roi);

    // Prefer projection-layer values; fall back to legacy when roiId absent.
    const structureCanBeEdited =
        detailView !== undefined
            ? detailView.isEditable
            : isApprovalStatusEditable(roi.approvalStatus);
    const isTarget = useDoseTargetLookup().has(roi.name);
    const structureCanBeDeleted = isApprovalStatusEditable(roi.approvalStatus);

    // volumeCm3 from the projection (already mm³÷1000); legacy path as fallback.
    const volumeCm3: number =
        detailView?.volumeCm3 != null
            ? detailView.volumeCm3
            : legacyGetRoiVolumeCm3();

    // Equivalent sphere diameter derived from volumeCm3 (sphere formula).
    const equivalentSphereDiameterCm: number =
        detailView !== undefined
            ? 2 * Math.cbrt((3 * volumeCm3) / (4 * Math.PI))
            : legacyGetEquivalentSphereDiameterCm();

    const hasContourData = !isRoiEmpty();
    const structureSetUid = selectedStructureSet?.dicomUid ?? '';

    const handleDelete = () => {
        setOpenedStructureDeletionConfirmationDialog(roi.roiNumber);
    };

    const handleFocus = () => {
        // Focusing on a hidden ROI shows nothing useful — flip visibility on
        // first, mirroring the inline focus icon behaviour in StructureField.
        if (!roi.visible) {
            setVisible(true);
        }
        if (roi.isPointRoi && roi.pointCoordinatesMm) {
            focusOnPoint(roi.pointCoordinatesMm);
        } else {
            focusOnStructure(roi.roiNumber);
        }
    };

    const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
    const isMenuOpen = Boolean(menuAnchorEl);

    const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
        setMenuAnchorEl(event.currentTarget);
    };

    const handleMenuClose = () => {
        setMenuAnchorEl(null);
    };

    const handleRename = () => {
        handleMenuClose();
        setIsRenaming(true);
    };

    const handleDuplicate = () => {
        if (!structureSetUid) {
            return;
        }
        handleMenuClose();
        copyRoi(structureSetUid);
    };

    const handleCopyOver = (targetRoiNumber: number) => {
        if (!structureSetUid) {
            return;
        }
        handleMenuClose();
        copySdfOver(structureSetUid, targetRoiNumber);
    };

    const otherRois = selectedStructureSet?.roiArray.filter(
        (r) => r.roiNumber !== roi.roiNumber
    );

    const content = roi ? (
        <CollapsibleBottomPanel
            header={
                <Box
                    ref={colorPickerAnchorRef}
                    display="grid"
                    gridTemplateColumns="auto 1fr"
                    alignItems="center"
                    gap={1}
                >
                    <StructureColorPicker
                        roi={roi}
                        onChange={setColor}
                        fieldAnchorRef={colorPickerAnchorRef}
                        disabled={!structureCanBeEdited}
                        popperPlacement="top-start"
                        isTarget={isTarget}
                    />
                    {isRenaming ? (
                        <InlineEdit
                            value={roi.name}
                            onSubmit={setName}
                            onCancel={() => setIsRenaming(false)}
                            validate={validateName}
                            fontSize={13}
                            fontWeight={500}
                        />
                    ) : (
                        <Typography
                            variant="body1"
                            fontSize={13}
                            fontWeight={500}
                            title={roi.name}
                            data-testid={`structure-details-name-${roi.name}`}
                            onDoubleClick={
                                structureCanBeEdited
                                    ? () => setIsRenaming(true)
                                    : undefined
                            }
                            sx={{
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                lineHeight: 1.2,
                                cursor: structureCanBeEdited
                                    ? 'pointer'
                                    : undefined,
                            }}
                        >
                            {roi.name}
                        </Typography>
                    )}
                </Box>
            }
            headerActions={
                <>
                    <Tooltip title={<FormattedMessage id="structure.focus" />}>
                        <IconButton
                            size="small"
                            onClick={handleFocus}
                            sx={{ p: 0.25 }}
                            data-testid={`structure-details-focus-button-${roi.name}`}
                            aria-label={intl.formatMessage({
                                id: 'structure.focus',
                            })}
                        >
                            <Focus size={12} />
                        </IconButton>
                    </Tooltip>
                    <Box
                        sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 0.5,
                        }}
                    >
                        {!roi.isPointRoi && (
                            <PopperPanel
                                testID={`structure-info-button-${roi.name}`}
                                icon={<Info size={12} />}
                                color="default"
                                ariaLabel={intl.formatMessage({
                                    id: 'properties',
                                })}
                                buttonTooltip={
                                    <FormattedMessage id="properties" />
                                }
                                title={<FormattedMessage id="properties" />}
                                width="320px"
                            >
                                <StructureDetailsInformation
                                    roi={roi}
                                    volumeCm3={volumeCm3}
                                    equivalentSphereDiameterCm={
                                        equivalentSphereDiameterCm
                                    }
                                />
                                {hasContourData && (
                                    <ContourStatistics
                                        roiNumber={roi.roiNumber}
                                    />
                                )}
                            </PopperPanel>
                        )}
                        <Tooltip
                            title={
                                structureCanBeEdited ? (
                                    <FormattedMessage id="structure.rename" />
                                ) : (
                                    <FormattedMessage id="structure.approved" />
                                )
                            }
                        >
                            <Typography
                                component="span"
                                sx={{ display: 'inline-flex' }}
                            >
                                <IconButton
                                    size="small"
                                    onClick={handleRename}
                                    disabled={!structureCanBeEdited}
                                    sx={{ p: 0.25 }}
                                    data-testid={`structure-edit-button-${roi.name}`}
                                    aria-label={intl.formatMessage({
                                        id: 'rename',
                                    })}
                                >
                                    <EditIcon size={12} />
                                </IconButton>
                            </Typography>
                        </Tooltip>
                        <IconButton
                            size="small"
                            onClick={handleMenuOpen}
                            sx={{ p: 0.25 }}
                            data-testid="structure-more-menu-button"
                            aria-label={intl.formatMessage({
                                id: 'moreActions',
                            })}
                        >
                            <MoreVertical size={12} />
                        </IconButton>
                    </Box>
                    <Menu
                        anchorEl={menuAnchorEl}
                        open={isMenuOpen}
                        onClose={handleMenuClose}
                        anchorOrigin={{
                            vertical: 'top',
                            horizontal: 'left',
                        }}
                        transformOrigin={{
                            vertical: 'top',
                            horizontal: 'left',
                        }}
                    >
                        {!roi.isPointRoi && (
                            <MenuItem
                                onClick={handleDuplicate}
                                disabled={!hasContourData || !structureSetUid}
                            >
                                <ListItemIcon>
                                    <CopyPlus size={12} />
                                </ListItemIcon>
                                <ListItemText>
                                    <FormattedMessage id="structure.duplicate" />
                                </ListItemText>
                            </MenuItem>
                        )}
                        {!roi.isPointRoi &&
                            otherRois &&
                            otherRois.length > 0 && (
                                <NestedMenuItem
                                    label={
                                        <FormattedMessage id="structure.replaceAnotherStructure" />
                                    }
                                    icon={<Replace size={12} />}
                                >
                                    {otherRois.map((target) => (
                                        <MenuItem
                                            key={target.roiNumber}
                                            onClick={() =>
                                                handleCopyOver(target.roiNumber)
                                            }
                                            disabled={
                                                !hasContourData ||
                                                !structureSetUid ||
                                                target.approvalStatus ===
                                                    DICOMApprovalStatus.APPROVED
                                            }
                                        >
                                            <ListItemText>
                                                {target.name}
                                            </ListItemText>
                                        </MenuItem>
                                    ))}
                                </NestedMenuItem>
                            )}
                        <MenuItem
                            onClick={() => {
                                handleMenuClose();
                                handleDelete();
                            }}
                            disabled={!structureCanBeDeleted}
                            data-testid="delete-structure-button"
                        >
                            <ListItemIcon>
                                <Trash2 size={12} />
                            </ListItemIcon>
                            <ListItemText>
                                <FormattedMessage id="structure.delete" />
                            </ListItemText>
                        </MenuItem>
                    </Menu>
                </>
            }
        >
            <StructureInformation
                roi={roi}
                isEditable={structureCanBeEdited}
                onInterpretedTypeChange={setInterpretedType}
                getRoiVolumeCm3={volumeCm3}
                onPointCoordinatesChange={setPointCoordinates}
            />
        </CollapsibleBottomPanel>
    ) : (
        <Typography variant="body2">
            No structure selected.
            <br />
            In order to start reviewing select one structure from the list.
        </Typography>
    );

    return <div>{content}</div>;
}
